/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { When } from "react-if";
import ReactJson from "react-json-view";
import styled from "styled-components";
import { Table, Text, Box } from "@nordcloud/gnui";
import { AuditLog } from "~/generated/graphql";
import { Sidebar } from "~/components";
import { FilterWrapper } from "~/components/SidebarWithMultiSelect/FilterWrapper";
import { useGNUITheme } from "~/hooks/useGNUITheme";
import { isValidJSONObject } from "~/utils/json";

type Props = {
  isOpen: boolean;
  open?: () => void;
  close: () => void;
  auditLog: AuditLog;
};

export function AuditLogDetailsSidebar({ isOpen, close, auditLog }: Props) {
  const { isLight } = useGNUITheme();

  return (
    <When condition={isOpen}>
      <Sidebar
        title="Audit Log Details"
        isOpen={isOpen}
        onClick={close}
        footer={<></>}
      >
        <FilterWrapper name="Tags" onClear={() => {}}>
          <Table
            hoverline
            striped
            css={{
              tableLayout: "fixed",
            }}
          >
            <Table.thead>
              <Table.tr>
                <Table.th>Key</Table.th>
                <Table.th>Value</Table.th>
              </Table.tr>
            </Table.thead>
            <Table.tbody>
              {auditLog.tags.map((tag) => (
                <Table.tr key={`{${tag.key}-${tag.value}}`}>
                  <StyledTd>{tag.key}</StyledTd>
                  <StyledTd>{tag.value}</StyledTd>
                </Table.tr>
              ))}
            </Table.tbody>
          </Table>
        </FilterWrapper>
        <FilterWrapper name="Data" onClear={() => {}}>
          <Box>
            {isValidJSONObject(auditLog.data) ? (
              <ReactJson
                src={JSON.parse(auditLog.data)}
                iconStyle="square"
                name={null}
                displayDataTypes={false}
                quotesOnKeys={false}
                style={isLight ? {} : { backgroundColor: "none" }}
                theme={isLight ? "rjv-default" : "grayscale"}
                collapsed
              />
            ) : (
              <Text>Data is not valid JSON!</Text>
            )}
          </Box>
        </FilterWrapper>
      </Sidebar>
    </When>
  );
}

const StyledTd = styled(Table.td)`
  white-space: normal;
  overflow-wrap: anywhere;
`;
